import type {
  JsonSchema,
  JsonSchemaProperty,
  UiSchema,
  UiFieldSchema,
  FieldConfig,
  FieldType,
  FieldOption,
  DependencyConfig,
} from '../types';

// ---------------------------------------------------------------------------
// Resolve field type from JSON Schema property + UI Schema hints
// ---------------------------------------------------------------------------

function resolveFieldType(
  prop: JsonSchemaProperty,
  uiField?: UiFieldSchema,
): FieldType {
  const widget = uiField?.['ui:widget'];

  switch (prop.type) {
    case 'boolean':
      return 'checkbox';

    case 'number':
    case 'integer':
      return 'number';

    case 'string':
    default: {
      // Enum fields
      if (prop.enum && prop.enum.length > 0) {
        if (widget === 'radio') return 'radio';
        return 'select';
      }

      // Widget overrides
      if (widget === 'textarea') return 'textarea';
      if (widget === 'password') return 'password';

      // Format-based
      if (prop.format === 'email') return 'email';
      if (prop.format === 'date') return 'date';
      if (prop.format === 'date-time') return 'datetime-local';
      if (prop.format === 'time') return 'time';

      return 'text';
    }
  }
}

// ---------------------------------------------------------------------------
// Build options list from enum + optional enumNames
// ---------------------------------------------------------------------------

function buildOptions(prop: JsonSchemaProperty): FieldOption[] | undefined {
  if (!prop.enum || prop.enum.length === 0) return undefined;

  return prop.enum.map((value, i) => ({
    label: prop.enumNames?.[i] ?? value,
    value,
  }));
}

// ---------------------------------------------------------------------------
// Parse dependencies into a map of child field -> DependencyConfig
// ---------------------------------------------------------------------------

function parseDependencies(schema: JsonSchema): Map<string, DependencyConfig> {
  const map = new Map<string, DependencyConfig>();

  if (!schema.dependencies) return map;

  for (const [parentField, dep] of Object.entries(schema.dependencies)) {
    // oneOf pattern: specific parent values trigger specific children
    if (dep.oneOf) {
      for (const branch of dep.oneOf) {
        const parentEnum = branch.properties?.[parentField]?.enum ?? [];
        const childFields = Object.keys(branch.properties ?? {}).filter(
          (key) => key !== parentField,
        );

        for (const childField of childFields) {
          const existing = map.get(childField);
          if (existing && existing.field === parentField) {
            // Merge values from multiple branches
            existing.values.push(...parentEnum);
          } else {
            map.set(childField, {
              field: parentField,
              values: [...parentEnum],
            });
          }
        }
      }
    }

    // Any-value pattern: children appear when parent has any value
    if (dep.properties) {
      const childFields = Object.keys(dep.properties).filter(
        (key) => key !== parentField,
      );
      for (const childField of childFields) {
        if (!map.has(childField)) {
          map.set(childField, { field: parentField, values: [] });
        }
      }
    }
  }

  return map;
}

// ---------------------------------------------------------------------------
// Determine field order from UI Schema
// ---------------------------------------------------------------------------

function resolveFieldOrder(
  propertyNames: string[],
  uiSchema: UiSchema,
): string[] {
  const uiOrder = uiSchema['ui:order'];
  if (!uiOrder || uiOrder.length === 0) return propertyNames;

  const ordered: string[] = [];
  const remaining = new Set(propertyNames);

  for (const entry of uiOrder) {
    if (entry === '*') {
      // Wildcard: insert all fields not explicitly listed
      for (const name of propertyNames) {
        if (remaining.has(name) && !uiOrder.includes(name)) {
          ordered.push(name);
          remaining.delete(name);
        }
      }
    } else if (remaining.has(entry)) {
      ordered.push(entry);
      remaining.delete(entry);
    }
  }

  // Append any remaining fields not covered by ui:order
  for (const name of remaining) {
    ordered.push(name);
  }

  return ordered;
}

// ---------------------------------------------------------------------------
// Main: Build an ordered array of FieldConfigs
// ---------------------------------------------------------------------------

export function buildFieldConfigs(
  schema: JsonSchema,
  uiSchema: UiSchema,
): FieldConfig[] {
  const requiredFields = new Set(schema.required ?? []);
  const dependencyMap = parseDependencies(schema);

  // Collect all property names including those only in dependency branches
  const allPropertyNames = new Set(Object.keys(schema.properties));

  // Add dependency-only properties
  if (schema.dependencies) {
    for (const dep of Object.values(schema.dependencies)) {
      if (dep.oneOf) {
        for (const branch of dep.oneOf) {
          for (const key of Object.keys(branch.properties ?? {})) {
            allPropertyNames.add(key);
          }
        }
      }
      if (dep.properties) {
        for (const key of Object.keys(dep.properties)) {
          allPropertyNames.add(key);
        }
      }
    }
  }

  // Merge dependency-branch properties into a lookup
  const allProperties: Record<string, JsonSchemaProperty> = {
    ...schema.properties,
  };
  if (schema.dependencies) {
    for (const dep of Object.values(schema.dependencies)) {
      if (dep.oneOf) {
        for (const branch of dep.oneOf) {
          for (const [key, prop] of Object.entries(branch.properties ?? {})) {
            if (!allProperties[key] && prop.type) {
              allProperties[key] = prop as JsonSchemaProperty;
            }
          }
        }
      }
      if (dep.properties) {
        for (const [key, prop] of Object.entries(dep.properties)) {
          if (!allProperties[key] && prop.type) {
            allProperties[key] = prop as JsonSchemaProperty;
          }
        }
      }
    }
  }

  const orderedNames = resolveFieldOrder(
    Array.from(allPropertyNames),
    uiSchema,
  );

  return orderedNames.map((name): FieldConfig => {
    const prop = allProperties[name];
    const uiField = uiSchema[name] as UiFieldSchema | undefined;
    const dependency = dependencyMap.get(name);

    // Check if this field is required by a dependency branch
    let isRequired = requiredFields.has(name);
    if (!isRequired && schema.dependencies) {
      for (const dep of Object.values(schema.dependencies)) {
        if (dep.oneOf) {
          for (const branch of dep.oneOf) {
            if (branch.required?.includes(name)) {
              isRequired = true;
              break;
            }
          }
        }
        if (isRequired) break;
        if (dep.required?.includes(name)) {
          isRequired = true;
          break;
        }
      }
    }

    const fieldType = resolveFieldType(prop, uiField);

    return {
      name,
      label: prop.title ?? name.charAt(0).toUpperCase() + name.slice(1),
      type: fieldType,
      required: isRequired,
      placeholder: uiField?.['ui:placeholder'],
      description: prop.description ?? uiField?.['ui:description'],
      disabled: uiField?.['ui:disabled'],
      readOnly: uiField?.['ui:readonly'],
      options: buildOptions(prop),
      min: prop.minimum ?? prop.exclusiveMinimum,
      max: prop.maximum ?? prop.exclusiveMaximum,
      step: prop.multipleOf,
      dependsOn: dependency,
    };
  });
}
